import { CUSTOM_TRACKER_OPTIONS } from 'utils/facades/integrations-facade';
import { getDeviceType } from 'utils/environment';

function _getDeviceType(): string {
	const type = getDeviceType();
	switch (type) {
		case 'desktop':
			return 'web';
		case 'tablet':
			return 'tabletApp';
		case 'mobile':
			return 'mobile';
		default:
			return type;
	}
}

export type ResolveCustomTrackerVariablesContext = {
	storyName: string;
	cardName: string;
	windowWidth?: number;
};

/**
 * Resolves CUSTOM_TRACKER_OPTIONS placeholders in a string to their actual values.
 *
 * @param text - The text containing placeholders to resolve
 * @param context - Context object with required storyName, cardName, and optional windowWidth
 * @returns The text with all placeholders resolved to their actual values
 *
 * @example
 * resolveCustomTrackerVariables(
 *   'https://tracker.com?platform=$PLATFORM&story=$STORY_NAME',
 *   { storyName: 'My Story', cardName: 'Card 1', windowWidth: 1920 }
 * )
 * @returns: 'https://tracker.com?platform=web&story=My Story'
 */
export function resolveCustomTrackerVariables(text: string, context: ResolveCustomTrackerVariablesContext): string {
	const { storyName, cardName, windowWidth } = context;
	const variables = Object.values(CUSTOM_TRACKER_OPTIONS).filter(({ value }) => text.includes(value));
	let resolvedText = text;

	variables.forEach(({ value }) => {
		let replacer: string;

		switch (value) {
			case '$STORY_NAME':
				replacer = storyName;
				break;
			case '$STORY_URL':
				replacer = typeof window !== 'undefined' ? window.location.href.replace(/\/c\/.*/, '') : '';
				break;
			case '$CARD_NAME':
				replacer = cardName;
				break;
			case '$PLATFORM':
				replacer = _getDeviceType();
				break;
			case '$REFERRER':
				replacer = typeof document !== 'undefined' ? document.referrer : '';
				break;
			case '$RESOLUTION':
				replacer = String(windowWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 0));
				break;
			default:
				replacer = '';
				break;
		}

		resolvedText = typeof resolvedText === 'string' ? resolvedText.replace(value, replacer) : String(resolvedText);
	});

	return resolvedText;
}
